import { useEffect, useRef } from "react";
import { OnboardingItemKey } from "../../../common/enums";
import { useOnboarding } from "../OnboardingProvider";

/**
 * Sets the help-step override for a page and auto-activates if the step is
 * still pending.  Cleans up the override on unmount.
 *
 * Use this for simple page-level guides that map 1-to-1 with a single
 * OnboardingItemKey.  For pages that switch between multiple step keys
 * (e.g. empty-state vs normal) handle the override manually.
 */
export function usePageGuide(stepKey: OnboardingItemKey) {
  const onboarding = useOnboarding();
  const hasAutoActivatedRef = useRef(false);

  const isActive = onboarding?.isActive ?? false;
  const pendingItemKeys = onboarding?.pendingItemKeys;
  const setHelpStepOverride = onboarding?.setHelpStepOverride;
  const activateStep = onboarding?.activateStep;

  // Always set the override so the "?" button works, even when isActive is false
  useEffect(() => {
    setHelpStepOverride?.(stepKey);
  }, [stepKey, setHelpStepOverride]);

  // Auto-activate only when onboarding is active and the step is pending
  useEffect(() => {
    if (!isActive) return;
    if (!hasAutoActivatedRef.current && pendingItemKeys?.has(stepKey)) {
      hasAutoActivatedRef.current = true;
      activateStep?.(stepKey);
    }
  }, [isActive, stepKey, pendingItemKeys, activateStep]);

  useEffect(() => {
    return () => setHelpStepOverride?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
